import './LoginContainer.scss';
import { Box, Button, Icons, Toggler, TopBar } from 'plume-ui';
import { BoxStyles } from 'plume-ui/dist/components/Box/Box';
import { TogglerElementTypes } from 'plume-ui/dist/components/Toggler/Toggler';
import { FunctionComponent, memo, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { DependencyContainer } from '../../../DependencyContainer';
import { MixPanelEvents } from '../../../mixPanelEvents';
import FormattedMessage from '../../../utils/components/FormattedMessage';
import { getIDPs, isRhoRegionalSignal } from '../../../utils/environments';
import { useRedirectToRoute } from '../../../utils/hooks/useRedirectToRoute';
import { useTrackEvent } from '../../trackingAnalytics/hooks/useTrackEvent';
import { AvailableScreens } from '../../trackingAnalytics/types';
import LoginContainerAuthError from '../components/LoginContainerAuthError';
import { LoginTogglerItems } from '../helpers';
import useLoginWithOkta from '../hooks/useLoginWithOkta';
import { LoginOptionKey } from '../types';

const { loginService } = new DependencyContainer();

const LoginContainer: FunctionComponent = () => {
  const { login, isAuthenticated } = useLoginWithOkta();
  const redirectToRoute = useRedirectToRoute();
  const authError = loginService.getAuthError();
  const { t } = useTranslation();
  const trackEvent = useTrackEvent();

  useEffect(() => {
    trackEvent({
      eventName: MixPanelEvents.SCREEN,
      additionalContent: {
        SCREEN: AvailableScreens.Login,
      },
    });
  }, []);

  if (isAuthenticated) {
    const targetRoute = loginService.getTargetRoute();
    redirectToRoute(targetRoute);
  }

  const [loginOption, setLoginOption] = useState<LoginOptionKey>('okta');
  const handleLoginToggle = (item: TogglerElementTypes) => {
    setLoginOption(item.key as LoginOptionKey);
  };

  const idps: { clientId: string }[] = getIDPs();
  const idp = idps[0]?.clientId;

  return (
    <>
      <Helmet>
        <title>{t('pageTitles.login')}</title>
      </Helmet>
      <TopBar
        brandingIcon={
          isRhoRegionalSignal() ? <Icons.JioIcon width={32} /> : undefined
        }
      />
      <div className="LoginContainer p-xl">
        <Box
          classes={(current: BoxStyles) => ({
            ...current,
            root: `${current.root} LoginContainer__box`,
          })}
        >
          <h1 className="LoginContainer__heading">
            <FormattedMessage id="signIn" />
          </h1>
          <h1 className="LoginContainer__app-name">{t('appName')}</h1>
          {!!authError && <LoginContainerAuthError {...authError} />}
          <div className="LoginContainer__login-button">
            {idp && (
              <Toggler
                variant="large"
                toggleElements={LoginTogglerItems(t)}
                onToggle={handleLoginToggle}
                value={loginOption}
              ></Toggler>
            )}
            <Button
              styleVariant="superprimary"
              onClick={() => login(loginOption, idp)}
            >
              <FormattedMessage id="signIn" />
            </Button>
          </div>
        </Box>
      </div>
    </>
  );
};

export default memo(LoginContainer);
